import { ApolloError } from "@apollo/client";
import * as Sentry from "@sentry/react";
import Amplitude from "amplitude-js";
import { version } from "../../package.json";
import { getStripe } from "./stripe";

export const amplitude = Amplitude.getInstance();

export function initThirdPartyApis() {
  initAmplitude();
  initSentry();
}

export async function redirectToStripeSession(sessionId: string) {
  const stripe = await getStripe();
  await stripe?.redirectToCheckout({ sessionId });
}

function initAmplitude() {
  if (!import.meta.env.VITE_AMPLITUDE_ID) return;

  const url = new URL(window.location.href);
  const deviceId = url.searchParams.get("adid") ?? "";

  amplitude.init(import.meta.env.VITE_AMPLITUDE_ID, undefined, { deviceId });

  if (deviceId) {
    url.searchParams.delete("adid");
    history.replaceState(null, "", url);
  }
}

function isAppFrame({ filename, abs_path }: Sentry.StackFrame) {
  const path = filename ?? abs_path ?? "";
  const origin = window.location.origin;
  // `blob:` covers our Lottie and Sentry replay compression workers.
  return path.startsWith(origin) || path.startsWith(`blob:${origin}`);
}

// Extensions, in-app browser bridges and native media controls throw through
// `window.onerror` with nothing of ours in the stack. Ours always has a frame.
function isEntirelyForeign(event: Sentry.Event) {
  const frames =
    event.exception?.values?.flatMap((v) => v.stacktrace?.frames ?? []) ?? [];
  return frames.length > 0 && !frames.some(isAppFrame);
}

// An exception with no frame at all cannot be traced to a line of ours, so there is
// nothing to act on: "<unknown>", "Type error" and bare RangeErrors from extension
// hosts all arrive this way.
function hasNoStack(event: Sentry.Event) {
  const values = event.exception?.values ?? [];
  return (
    values.length > 0 &&
    values.every((v) => (v.stacktrace?.frames?.length ?? 0) === 0)
  );
}

// An ApolloError carries nothing of ours in its stack, so Sentry groups every one of them
// into a single issue that takes the title of whatever failed last - a paywall rejection,
// a refused photo and a broken mutation all in the same pile. The code mojo_api tags the
// error with tells them apart; without one we leave Sentry's own grouping alone.
function serverErrorFingerprint(hint?: Sentry.EventHint) {
  const error = hint?.originalException;
  if (!(error instanceof ApolloError)) return undefined;
  const code = error.graphQLErrors
    .map((e) => e.extensions?.code)
    .find((c): c is string => typeof c === "string" && c.length > 0);
  return code ? ["ApolloError", code] : undefined;
}

function initSentry() {
  if (!import.meta.env.VITE_SENTRY_DSN) return;

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [Sentry.replayIntegration()],
    release: version,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Conditions of the visitor's browser or network, not defects: there is no
    // change we could make to any of these, and the UI already recovers.
    ignoreErrors: [
      // The user cleared site data, or the browser evicted the database
      /Database deleted by request of the user/,
      /Connection to Indexed Database server lost/,
      // The paywall, handled in the UI
      /Not enough AI credits/,
      // OpenAI declined the photo the user picked. The UI already says so in their own
      // language, and no change here would make the safety system accept it.
      /moderation_blocked/,
      // Sign-in attempted with no usable connection
      /auth\/network-request-failed/,
      // A hashed chunk that no longer exists after a deploy - reloaded below
      /Failed to fetch dynamically imported module/,
      /Importing a module script failed/,
      // Offline or cancelled requests. Apollo surfaces the ones that matter
      /Failed to fetch/,
      /Load failed/,
      // Replay probing an ad or embed iframe it is not allowed to read
      /Blocked a frame with origin/,
      // Extension code injected into the page
      /This script should only be loaded in a browser extension/,
    ],
    beforeSend: (event, hint) => {
      if (isEntirelyForeign(event) || hasNoStack(event)) return null;
      const fingerprint = serverErrorFingerprint(hint);
      return fingerprint ? { ...event, fingerprint } : event;
    },
  });
}
